import React from "react";
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { auth, authPersistenceReady } from "./firebase";
import { ShieldCheck, Clock3, Send, LogOut, RefreshCw, AlertCircle } from "lucide-react";

/*
 * Google-only sign-in and approval gate.
 * Firebase authenticates the user; every access/approval request is sent through
 * the admin-control proxy with a Firebase ID token. The proxy/backend MUST verify
 * that token with Firebase Admin SDK and enforce approval on every protected API.
 */

const PROXY_BASE_URL = "https://admincontrolproxy-kinm2tdd4q-uc.a.run.app";
const API = {
  signIn: `${PROXY_BASE_URL}/api/access/sign-in`,
  me: `${PROXY_BASE_URL}/api/access/me`,
  request: `${PROXY_BASE_URL}/api/access/approval-request`,
  signOut: `${PROXY_BASE_URL}/api/access/sign-out`,
};

async function callApi(path, firebaseUser, options = {}) {
  if (!firebaseUser) throw new Error("Sign in with Google to continue.");
  const idToken = await firebaseUser.getIdToken();
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || data.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

const normalizeStatus = (value) => {
  const status = String(value || "not_requested").toLowerCase().replaceAll("-", "_");
  return ["not_requested", "pending", "approved", "rejected"].includes(status)
    ? status : "not_requested";
};

export default function AccessGate({ children }) {
  const [checking, setChecking] = React.useState(true);
  const [firebaseUser, setFirebaseUser] = React.useState(null);
  const [user, setUser] = React.useState(null);
  const [status, setStatus] = React.useState("signed_out");
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [requestMessage, setRequestMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const applySession = React.useCallback((data, fallbackUser = null) => {
    const nextUser = data?.user || data?.account || (fallbackUser ? {
      uid: fallbackUser.uid,
      email: fallbackUser.email,
      displayName: fallbackUser.displayName,
      photoURL: fallbackUser.photoURL,
    } : null);
    const nextStatus = normalizeStatus(data?.approvalStatus || data?.accessStatus || data?.approval?.status);
    setUser(nextUser);
    setStatus(nextUser ? nextStatus : "signed_out");
    setRejectionReason(data?.rejectionReason || data?.approval?.reason || "");
  }, []);

  const refreshSession = React.useCallback(async (activeUser) => {
    if (!activeUser) return;
    setError("");
    try {
      const data = await callApi(API.me, activeUser);
      applySession(data, activeUser);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        setUser(null);
        setStatus("signed_out");
        setRejectionReason("");
        setError("The proxy could not verify your Google account. Check its /api/access route and Firebase token verification.");
      } else {
        setError("Could not verify your access through the admin proxy. Check the backend connection and try again.");
      }
    } finally {
      setChecking(false);
    }
  }, [applySession]);

  React.useEffect(() => {
    let active = true;
    authPersistenceReady.catch(() => {});
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!active) return;
      setFirebaseUser(nextUser);
      setError("");
      if (nextUser) {
        setChecking(true);
        await refreshSession(nextUser);
      } else {
        setUser(null);
        setStatus("signed_out");
        setRejectionReason("");
        setChecking(false);
      }
    });
    return () => { active = false; unsubscribe(); };
  }, [refreshSession]);

  async function handleGoogleSignIn() {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await authPersistenceReady;
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(auth, provider);
      const data = await callApi(API.signIn, result.user, {
        method: "POST",
        body: JSON.stringify({ idToken: await result.user.getIdToken() }),
      });
      applySession(data, result.user);
      setNotice(data.message || "Google sign-in successful. Check your access status below.");
    } catch (e) {
      setError(e.code === "auth/popup-closed-by-user"
        ? "Google sign-in was cancelled. Please try again."
        : (e.message || "Google sign-in failed."));
    } finally {
      setBusy(false);
      setChecking(false);
    }
  }

  async function submitApprovalRequest() {
    if (busy || !firebaseUser) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const data = await callApi(API.request, firebaseUser, {
        method: "POST",
        body: JSON.stringify({ message: requestMessage.trim() }),
      });
      applySession({
        user: data.user || user,
        approvalStatus: data.approvalStatus || "pending",
        rejectionReason: data.rejectionReason || "",
      }, firebaseUser);
      setRequestMessage("");
      setNotice(data.message || "Approval request sent to the administrator.");
    } catch (e) {
      setError(e.message || "Could not submit your approval request.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setBusy(true);
    setError("");
    try {
      if (firebaseUser) {
        try { await callApi(API.signOut, firebaseUser, { method: "POST" }); } catch (_) {}
      }
      await signOut(auth);
    } catch (e) {
      setError(e.message || "Could not sign out.");
    } finally {
      setUser(null);
      setStatus("signed_out");
      setFirebaseUser(null);
      setRejectionReason("");
      setBusy(false);
    }
  }

  if (checking) {
    return <Screen><div style={styles.card}><div style={styles.brand}><ShieldCheck size={24}/><span>PITICS Creative Tools</span></div><p style={styles.muted}>Checking Google sign-in and approval status…</p></div></Screen>;
  }

  if (firebaseUser && user && status === "approved") return <>{children}</>;

  return (
    <Screen>
      <div style={styles.card}>
        <div style={styles.brand}><ShieldCheck size={26}/><span>PITICS Creative Tools</span></div>
        <p style={styles.intro}>Continue with Google. Access to the tools is enabled only after an administrator approves your request.</p>
        {error && <div role="alert" style={styles.error}><AlertCircle size={17}/><span>{error}</span></div>}
        {notice && <div role="status" style={styles.notice}>{notice}</div>}

        {!firebaseUser ? (
          <div>
            <h1 style={styles.heading}>Sign in</h1>
            <button style={styles.primaryButton} type="button" onClick={handleGoogleSignIn} disabled={busy}>
              <GoogleMark />{busy ? "Connecting to Google…" : "Continue with Google"}
            </button>
            <p style={styles.footnote}>Only Google authentication is supported. If you use the correct Google account but still need access, submit an approval request after signing in.</p>
          </div>
        ) : (
          <div>
            <div style={styles.accountRow}>
              <div style={styles.identity}>
                {user?.photoURL && <img src={user.photoURL} alt="" style={styles.avatar}/>}
                <div><div style={styles.smallLabel}>GOOGLE ACCOUNT</div><strong>{user?.displayName || firebaseUser.displayName || user?.email || firebaseUser.email || "Google account"}</strong><div style={styles.muted}>{user?.email || firebaseUser.email || ""}</div></div>
              </div>
              <button type="button" style={styles.textButton} onClick={handleSignOut} disabled={busy}><LogOut size={15}/> Sign out</button>
            </div>

            {status === "not_requested" && <>
              <div style={styles.statusTitle}><AlertCircle size={21}/> Approval required</div>
              <p style={styles.body}>Your Google account is signed in, but it cannot open any tools until you send an access request and an administrator approves it.</p>
              <label style={styles.label}>Message to administrator (optional)
                <textarea style={styles.textarea} value={requestMessage} onChange={e => setRequestMessage(e.target.value)} maxLength={1000} placeholder="Tell the administrator why you need access…"/>
              </label>
              <button type="button" style={styles.primaryButton} onClick={submitApprovalRequest} disabled={busy}><Send size={16}/>{busy ? "Submitting…" : "Send approval request"}</button>
            </>}

            {status === "pending" && <>
              <div style={styles.statusTitle}><Clock3 size={22}/> Request pending</div>
              <p style={styles.body}>Your approval request has been submitted. The tools will become available after an administrator approves it.</p>
              <button style={styles.secondaryButton} onClick={() => refreshSession(firebaseUser)} disabled={busy}><RefreshCw size={15}/> Check approval status</button>
            </>}

            {status === "rejected" && <>
              <div style={styles.statusTitle}><AlertCircle size={22}/> Request not approved</div>
              <p style={styles.body}>Your access request was not approved. Contact the administrator if you need clarification.</p>
              {rejectionReason && <div style={styles.reason}><strong>Administrator's note</strong><p>{rejectionReason}</p></div>}
              <button style={styles.secondaryButton} onClick={() => refreshSession(firebaseUser)} disabled={busy}><RefreshCw size={15}/> Refresh status</button>
            </>}
          </div>
        )}
        <div style={styles.security}><ShieldCheck size={14}/> Google identity and approval must be verified by the server, not just by this page.</div>
      </div>
    </Screen>
  );
}

function GoogleMark() {
  return <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.76 7.18l7.73 6C44.42 37.96 46.98 31.84 46.98 24.55z"/>
    <path fill="#FBBC05" d="M10.54 28.59A14.4 14.4 0 0 1 9.75 24c0-1.59.27-3.13.76-4.59l-7.98-6.19A23.9 23.9 0 0 0 0 24c0 3.87.93 7.53 2.56 10.78l7.98-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.91-5.8l-7.73-6c-2.14 1.45-4.88 2.3-8.18 2.3-6.26 0-11.57-4.22-13.46-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>;
}

function Screen({ children }) {
  return <main style={styles.screen}>{children}</main>;
}

const styles = {
  screen: { minHeight: "100vh", boxSizing: "border-box", display: "grid", placeItems: "center", padding: 24, background: "linear-gradient(135deg, #eaf3ff 0%, #f7faff 48%, #e8fbf7 100%)", fontFamily: "Inter, Segoe UI, Arial, sans-serif" },
  card: { width: "100%", maxWidth: 440, boxSizing: "border-box", padding: 30, borderRadius: 20, background: "#fff", boxShadow: "0 18px 55px rgba(20, 48, 90, .13)", border: "1px solid #e3ebf5" },
  brand: { display: "flex", alignItems: "center", gap: 10, color: "#1455a5", fontWeight: 800, fontSize: 19, marginBottom: 14 },
  intro: { color: "#56677c", lineHeight: 1.55, fontSize: 14, margin: "0 0 22px" },
  heading: { fontSize: 25, color: "#172b4d", margin: "0 0 20px" },
  label: { display: "block", color: "#263a53", fontSize: 13, fontWeight: 650, margin: "15px 0" },
  textarea: { display: "block", boxSizing: "border-box", width: "100%", minHeight: 90, marginTop: 7, padding: 12, resize: "vertical", border: "1px solid #cbd7e6", borderRadius: 10, font: "inherit", fontWeight: 400, outlineColor: "#2875d5" },
  primaryButton: { width: "100%", border: 0, borderRadius: 10, padding: "13px 16px", display: "flex", justifyContent: "center", alignItems: "center", gap: 9, background: "#155fc0", color: "#fff", fontWeight: 750, fontSize: 14, cursor: "pointer", marginTop: 18, opacity: 1 },
  secondaryButton: { border: "1px solid #c8d7ea", borderRadius: 10, padding: "11px 14px", display: "inline-flex", alignItems: "center", gap: 8, background: "#f7faff", color: "#1e4f8a", fontWeight: 700, cursor: "pointer" },
  textButton: { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #d7e0eb", borderRadius: 8, background: "#fff", padding: "8px 10px", cursor: "pointer", color: "#344a63", whiteSpace: "nowrap" },
  muted: { color: "#718096", fontSize: 13, margin: "5px 0" },
  body: { color: "#53657b", lineHeight: 1.6, fontSize: 14 },
  smallLabel: { color: "#7b8ba0", fontSize: 10, fontWeight: 800, letterSpacing: 1, marginBottom: 5 },
  accountRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 0 18px", borderBottom: "1px solid #e9eef5", marginBottom: 18, fontSize: 14 },
  identity: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  avatar: { width: 38, height: 38, borderRadius: "50%", objectFit: "cover" },
  statusTitle: { display: "flex", alignItems: "center", gap: 9, color: "#194f8c", fontWeight: 800, fontSize: 19, margin: "8px 0 12px" },
  reason: { background: "#fff7ed", border: "1px solid #fed7aa", padding: 12, borderRadius: 10, fontSize: 13, color: "#7c2d12" },
  error: { display: "flex", gap: 8, alignItems: "flex-start", padding: 12, borderRadius: 10, background: "#fff1f2", color: "#9f1239", fontSize: 13, marginBottom: 14 },
  notice: { padding: 11, borderRadius: 10, background: "#eff6ff", color: "#1d4ed8", fontSize: 13, marginBottom: 14 },
  footnote: { color: "#73839a", fontSize: 12, lineHeight: 1.5, marginBottom: 0 },
  security: { display: "flex", alignItems: "center", gap: 7, color: "#75859a", borderTop: "1px solid #edf1f6", paddingTop: 14, marginTop: 24, fontSize: 11 },
};
